import React, { useState, useEffect } from 'react';
import {
  X,
  Search,
  Package,
  ArrowRight,
  Minus,
  Plus,
  Loader2,
  Filter,
} from 'lucide-react';
import { materialReturnService } from '../../services/materialReturnService';
import Modal from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormField, Input, Textarea } from '@/components/ui/FormComponents';

export const ReturnMaterialsModal = ({ isOpen, onClose, project, onSuccess, setToast }) => {
  const [siteInventory, setSiteInventory] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState('All Roles');
  const [formData, setFormData] = useState({
    driverName: '',
    vehicleNumber: '',
    notes: ''
  });

  useEffect(() => {
    if (isOpen && project?._id) {
      fetchSiteInventory();
    }
  }, [isOpen, project?._id]);

  const fetchSiteInventory = async () => {
    setLoading(true);
    try {
      const data = await materialReturnService.getSiteInventory(project._id);
      setSiteInventory(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching site inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (item) => {
    const key = `${item.inventoryId}_${item.role}`;
    const existingIdx = selectedItems.findIndex(i => `${i.inventoryId}_${i.role}` === key);
    if (existingIdx > -1) return;

    setSelectedItems([...selectedItems, {
      inventoryId: item.inventoryId,
      itemName: item.itemName,
      role: item.role,
      quantity: 1,
      availableAtSite: item.availableAtSite
    }]);
  };

  const handleUpdateQuantity = (idx, delta) => {
    const updated = [...selectedItems];
    const newQty = updated[idx].quantity + delta;
    if (newQty >= 1 && newQty <= updated[idx].availableAtSite) {
      updated[idx].quantity = newQty;
      setSelectedItems(updated);
    }
  };

  const handleRemoveItem = (idx) => {
    setSelectedItems(selectedItems.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (selectedItems.length === 0) return;

    setSubmitting(true);
    try {
      await materialReturnService.createReturn({
        returnType: 'SITE',
        project: project._id,
        items: selectedItems.map(i => ({
          inventoryId: i.inventoryId,
          itemName: i.itemName,
          role: i.role,
          quantity: i.quantity
        })),
        ...formData
      });
      if (onSuccess) onSuccess();
      onClose();
      setSelectedItems([]);
      setFormData({ driverName: '', vehicleNumber: '', notes: '' });
    } catch (error) {
      const msg = error?.response?.data?.message || error.message || 'Could not submit return request';
      if (setToast) setToast({ message: msg, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const inventoryList = Array.isArray(siteInventory) ? siteInventory : [];
  
  const uniqueRoles = Array.from(new Set(inventoryList.map(i => i.role).filter(Boolean))).map(r => r.charAt(0).toUpperCase() + r.slice(1)).sort();

  const filteredInventory = inventoryList.filter((item) => {
    const matchesSearch = String(item?.itemName || '').toLowerCase().includes(search.toLowerCase());
    const itemRole = item.role ? item.role.charAt(0).toUpperCase() + item.role.slice(1) : 'Unassigned';
    const matchesRole = selectedRole === 'All Roles' || itemRole === selectedRole;
    return matchesSearch && matchesRole;
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Return Materials"
      size="xl"
      footer={
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 w-full justify-end">
          <Button
            type="button"
            variant="secondary"
            className="w-full sm:w-auto h-10 px-8 rounded-lg"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={submitting}
            disabled={selectedItems.length === 0}
            onClick={handleSubmit}
            className="w-full sm:w-auto h-10 px-8 rounded-lg"
          >
            {submitting ? 'Submitting...' : 'Submit Return Request'}
            {!submitting && selectedItems.length > 0 && (
              <div className="ml-2 w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[10px]">
                {selectedItems.length}
              </div>
            )}
          </Button>
        </div>
      }
    >
      <div className="flex overflow-hidden h-[62vh] min-h-[460px] -mx-5 sm:-mx-6 -my-5 sm:-my-6 font-poppins">

        {/* Left Side: Selected Items & Form */}
        <div className="w-[55%] border-r border-slate-100 flex flex-col bg-white">
          <div className="p-6 pb-2 flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[11px] font-bold text-brand-blue uppercase tracking-wider">
                Selected items ({selectedItems.length})
              </h3>
              {selectedItems.length > 0 && (
                <button
                  onClick={() => setSelectedItems([])}
                  className="text-[10px] font-bold text-slate-400 hover:text-rose-500 transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto pr-1 no-scrollbar flex flex-col gap-2 min-h-0">
              {selectedItems.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center py-10 bg-slate-50/50 border border-dashed border-slate-200 rounded-xl">
                  <Package className="w-9 h-9 text-slate-200 mb-2" />
                  <p className="text-[11px] font-medium text-slate-400 text-center px-10 leading-relaxed">
                    No items selected. Click an item from the right panel to add.
                  </p>
                </div>
              ) : (
                selectedItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-white border border-slate-100 p-3.5 rounded-xl flex items-center justify-between hover:border-blue-200 transition-colors shrink-0"
                  >
                    <div className="flex-1 min-w-0">
                      <h4 className="text-[13px] font-semibold text-slate-700 tracking-tight truncate">{item.itemName}</h4>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-slate-400">Dispatched: {item.availableAtSite} units</span>
                        {item.role && item.role !== 'Unassigned' && (
                          <span className="text-[9px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded font-medium">{item.role.charAt(0).toUpperCase() + item.role.slice(1)}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-4 shrink-0">
                      <div className="flex items-center bg-slate-50 rounded-md p-0.5 border border-slate-200 shadow-sm">
                        <button
                          onClick={() => handleUpdateQuantity(idx, -1)}
                          className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-brand-blue disabled:opacity-20 transition-colors cursor-pointer"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center text-[12px] font-semibold text-slate-800 tabular-nums">{item.quantity}</span>
                        <button
                          onClick={() => handleUpdateQuantity(idx, 1)}
                          className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-brand-blue disabled:opacity-20 transition-colors cursor-pointer"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <button
                        onClick={() => handleRemoveItem(idx)}
                        className="text-slate-300 hover:text-rose-500 transition-colors p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Logistics Form */}
          <div className="p-6 pt-4 border-t border-slate-100 bg-white shrink-0 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Driver Name" required>
                <Input
                  type="text"
                  value={formData.driverName}
                  onChange={(e) => setFormData({ ...formData, driverName: e.target.value })}
                  placeholder="Enter driver name"
                />
              </FormField>
              <FormField label="Vehicle Number" required>
                <Input
                  type="text"
                  value={formData.vehicleNumber}
                  onChange={(e) => setFormData({ ...formData, vehicleNumber: e.target.value })}
                  placeholder="e.g. TS 01 AB 1234"
                  className="uppercase"
                />
              </FormField>
            </div>
            <FormField label="Return Notes">
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Add any specific details regarding the return..."
                rows={2}
              />
            </FormField>
          </div>
        </div>

        {/* Right Side: Dispatched Items */}
        <div className="w-[45%] flex flex-col bg-slate-50/30 overflow-hidden">
          <div className="p-6 pb-3 border-b border-slate-100 bg-white shrink-0">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[11px] font-bold text-brand-blue uppercase tracking-wider">
                Dispatched items
              </h3>
              <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-600 border border-emerald-100">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[9px] font-bold tracking-wider">Dispatched stock</span>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search dispatched items..."
                  className="w-full h-[35px] bg-white border border-slate-200 pl-9 pr-4 rounded-md text-[12px] text-slate-700 focus:outline-none focus:ring-1 focus:border-brand-blue focus:ring-brand-blue/30 transition-all placeholder:text-slate-400"
                />
              </div>
              <div className="relative shrink-0">
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="h-[35px] bg-white border border-slate-200 px-3 pr-8 rounded-md text-[12px] font-medium text-slate-700 outline-none focus:border-brand-blue transition-all cursor-pointer appearance-none"
                >
                  <option value="All Roles">All Roles</option>
                  {uniqueRoles.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                <Filter className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2 no-scrollbar">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3 opacity-40">
                <Loader2 className="w-7 h-7 text-brand-blue animate-spin" />
                <p className="text-[11px] font-medium text-slate-500">Scanning site materials...</p>
              </div>
            ) : filteredInventory.length > 0 ? (
              filteredInventory.map((item) => (
                <button
                  key={item.inventoryId}
                  onClick={() => handleSelectItem(item)}
                  className="w-full p-3.5 border border-slate-100 bg-white rounded-xl flex items-center justify-between hover:border-brand-blue/30 hover:shadow-sm transition-all text-left group shrink-0"
                >
                  <div className="flex-1 min-w-0">
                    <h4 className="text-[12px] font-semibold text-slate-700 tracking-tight truncate group-hover:text-brand-blue transition-colors">
                      {item.itemName}
                    </h4>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-slate-400">Dispatched: {item.availableAtSite} units</span>
                      {item.role && item.role !== 'Unassigned' && (
                        <span className="text-[9px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded font-medium">{item.role.charAt(0).toUpperCase() + item.role.slice(1)}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4 shrink-0">
                    <div className="flex flex-col items-end">
                      <span className="text-[16px] font-bold text-brand-blue tabular-nums">{item.availableAtSite}</span>
                      <span className="text-[9px] text-slate-400">Available</span>
                    </div>
                    <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center group-hover:bg-brand-blue transition-all border border-slate-200 group-hover:border-brand-blue">
                      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-white transition-colors" />
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-20 opacity-30">
                <Package className="w-10 h-10 text-slate-300 mb-2" />
                <p className="text-[11px] font-medium text-slate-500 text-center">No dispatched materials found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
